import { useAuth } from '@teko/auth-react';
import { Form, styled } from '@teko/ds-desktop';
import { Area } from '@teko/location';
import { NewShipmentAddress } from 'components/NewShipmentAddress';
import { MODE_TERMINAL_PICKER, useDistanceTerminalContext } from 'contexts';
import { useAddresses, useLocationsV2 } from 'hooks';
import { useState } from 'react';
import { useGetDistanceTerminal } from '../useGetDistanceTerminal';
import { STEP_LOCATION, usePickLocation } from '../usePickLocation';
import { HeaderModal } from './HeaderModal';
import { EnterAddress } from './StepEnterAddress/EnterAddress';
import { PickAddress } from './StepEnterAddress/PickAddress';

export interface AddressForm {
  address: string;
  provinceCode: string;
  wardCode: string;
  districtCode: string;
  addressNote: string;
}

export const ModeEnterAddressV2 = ({
  onClosePopup,
}: {
  onClosePopup: () => void;
}) => {
  const [form] = Form.useForm<AddressForm>();

  const {
    isGetCurrentLocation,
    setModeTerminalPicker,
    temporaryDataDistanceTerminal,
    setTemporaryDataDistanceTerminal,
    editingAddress,
    setEditingAddress,
    location,
    resetSelectedAddress,
  } = useDistanceTerminalContext();
  const { user } = useAuth();

  const { getDistanceTerminalsByGeoLocation, getDistanceTerminalsByAddress } =
    useGetDistanceTerminal();

  const {
    provinces = [],
    wards = [],
    province,
    ward,
    loadingLocation,
    onProvinceChanged,
    onWardChanged,
  } = useLocationsV2();

  const [stepLocation, setStepLocation] = useState<STEP_LOCATION>('ADDRESS');
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });
  const { addresses } = useAddresses();

  const {
    headerText,
    backToPreviousLevelLocation,
    keyword,
    setKeyword,
    filterLocations,
  } = usePickLocation({
    stepLocation,
    location: { provinceName: province?.title || '' },
    setStepLocation,
    provinces,
    wards,
  });

  const displayBackButton = stepLocation !== 'ADDRESS';

  const resetPosition = () => {
    setPlace({ lat: undefined, long: undefined });
  };

  const onSelectLocation = (location: Area) => {
    if (stepLocation === 'PROVINCE') {
      onProvinceChanged(location.code);
      form.setFieldsValue({
        provinceCode: location.code,
        wardCode: '',
      });
      onWardChanged('');
      resetPosition();
      setStepLocation('WARD');
      return;
    }
    onWardChanged(location.code);
    form.setFieldsValue({
      wardCode: location.code,
    });
    resetPosition();
    setStepLocation('ADDRESS');
  };

  const onFinishFormEnterAddress = (address: AddressForm) => {
    if (isGetCurrentLocation) {
      getDistanceTerminalsByGeoLocation();
      return;
    }

    const provinceName =
      provinces.find((province) => province.code === address.provinceCode)
        ?.title || '';
    const wardName =
      wards.find((ward) => ward.code === address.wardCode)?.title || '';
    getDistanceTerminalsByAddress({
      ...address,
      provinceName,
      wardName,
      lat: place.lat,
      long: place.long,
      addressNote: address.addressNote,
    });
  };

  return (
    <>
      <HeaderModal
        displayBackButton={displayBackButton}
        backToPreviousLevelLocation={backToPreviousLevelLocation}
        onClosePopup={onClosePopup}
        title={headerText.title}
        subTitle={headerText.sutTitle}
      />
      <StyledBody>
        {user?.userId ? (
          <NewShipmentAddress
            editingAddress={editingAddress}
            hideCancelButton={!addresses?.length}
            onClose={(refetch, newAddress) => {
              const isUpdatingAddress = !!editingAddress;
              setEditingAddress(undefined);
              if (refetch && newAddress) {
                setTemporaryDataDistanceTerminal?.({
                  ...temporaryDataDistanceTerminal,
                  location: {
                    address: newAddress.address || '',
                    provinceName: newAddress.provinceName || '',
                    wardName: newAddress.wardName || '',
                    provinceCode: newAddress.provinceCode,
                    wardCode: newAddress.wardCode,
                    districtCode: newAddress.districtCode,
                    districtName: newAddress.districtName,
                    addressId: newAddress.addressId,
                    addressNote:
                      newAddress.addressNote || newAddress.note || '',
                    lat: newAddress.latitude
                      ? Number(newAddress.latitude)
                      : undefined,
                    long: newAddress.longitude
                      ? Number(newAddress.longitude)
                      : undefined,
                  },
                });
                if (isUpdatingAddress) {
                  if (location?.addressId === newAddress.addressId) {
                    resetSelectedAddress?.();
                  } else {
                    setModeTerminalPicker(MODE_TERMINAL_PICKER.LIST_ADDRESS);
                  }
                  return;
                }
                getDistanceTerminalsByAddress({
                  address: newAddress.address || '',
                  provinceName: newAddress.provinceName || '',
                  wardName: newAddress.wardName || '',
                  provinceCode: newAddress.provinceCode,
                  wardCode: newAddress.wardCode,
                  districtCode: newAddress.districtCode,
                  districtName: newAddress.districtName,
                  addressId: newAddress.addressId,
                  addressNote: newAddress.addressNote || newAddress.note || '',
                  lat: newAddress.latitude
                    ? Number(newAddress.latitude)
                    : undefined,
                  long: newAddress.longitude
                    ? Number(newAddress.longitude)
                    : undefined,
                });
              } else {
                setModeTerminalPicker(MODE_TERMINAL_PICKER.LIST_ADDRESS);
              }
            }}
          />
        ) : (
          <>
            {stepLocation === 'ADDRESS' && (
              <EnterAddress
                province={province}
                ward={ward}
                setStepLocation={setStepLocation}
                form={form}
                setPlace={setPlace}
                onFinish={onFinishFormEnterAddress}
              />
            )}
            {stepLocation !== 'ADDRESS' && (
              <PickAddress
                placeHolderSearch={headerText.placeHolderSearch}
                keyword={keyword}
                setKeyword={setKeyword}
                filterLocations={filterLocations}
                loadingLocation={loadingLocation}
                onSelectLocation={onSelectLocation}
                selectedValue={
                  (stepLocation === 'PROVINCE' && province?.code) ||
                  (stepLocation === 'WARD' && ward?.code) ||
                  ''
                }
              />
            )}
          </>
        )}
      </StyledBody>
    </>
  );
};

const StyledBody = styled.div`
  margin-top: 16px;
`;
